import React from 'react';
import { matchPath, useHistory, useLocation } from 'react-router';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';

import {
    StandaloneBarsRegularIcon,
    StandaloneChartAreaFillIcon,
    StandaloneChartAreaRegularIcon,
    StandaloneClockThreeFillIcon,
    StandaloneClockThreeRegularIcon,
    StandaloneHouseBlankFillIcon,
    StandaloneHouseBlankRegularIcon,
} from '@deriv/quill-icons';
import { routes } from '@deriv/shared';
import { useStore } from '@deriv/stores';
import { Badge, Navigation } from '@deriv-com/quill-ui';
import { Localize } from '@deriv-com/translations';

type BottomNavProps = {
    className?: string;
};

const BottomNav = observer(({ className }: BottomNavProps) => {
    const history = useHistory();
    const location = useLocation();
    const { client, portfolio } = useStore();
    const { active_positions_count } = portfolio;
    const { is_logged_in } = client;

    const bottomNavItems = React.useMemo(
        () => [
            {
                icon: <StandaloneHouseBlankRegularIcon iconSize='sm' fill='var(--color-text-primary)' />,
                activeIcon: <StandaloneHouseBlankFillIcon iconSize='sm' />,
                label: <Localize i18n_default_text='Home' />,
                path: null,
                action: 'home' as const,
            },
            {
                icon: <StandaloneChartAreaRegularIcon iconSize='sm' fill='var(--color-text-primary)' />,
                activeIcon: <StandaloneChartAreaFillIcon iconSize='sm' />,
                label: <Localize i18n_default_text='Trade' />,
                path: routes.index,
            },
            ...(is_logged_in
                ? [
                      {
                          icon:
                              active_positions_count > 0 ? (
                                  <Badge
                                      variant='notification'
                                      position='top-right'
                                      label={active_positions_count.toString()}
                                      color='danger'
                                      size='sm'
                                      contentSize='sm'
                                      className='bottom-nav-item__position-badge'
                                  >
                                      <StandaloneClockThreeRegularIcon iconSize='sm' fill='var(--color-text-primary)' />
                                  </Badge>
                              ) : (
                                  <StandaloneClockThreeRegularIcon iconSize='sm' fill='var(--color-text-primary)' />
                              ),
                          activeIcon:
                              active_positions_count > 0 ? (
                                  <Badge
                                      variant='notification'
                                      position='top-right'
                                      label={active_positions_count.toString()}
                                      color='danger'
                                      size='sm'
                                      contentSize='sm'
                                      className='bottom-nav-item__position-badge'
                                  >
                                      <StandaloneClockThreeFillIcon iconSize='sm' fill='var(--color-text-primary)' />
                                  </Badge>
                              ) : (
                                  <StandaloneClockThreeFillIcon iconSize='sm' />
                              ),
                          label: (
                              <React.Fragment>
                                  <span className='user-guide__anchor' />
                                  <Localize i18n_default_text='Positions' />
                              </React.Fragment>
                          ),
                          path: routes.trader_positions,
                      },
                  ]
                : []),
            {
                icon: <StandaloneBarsRegularIcon iconSize='sm' fill='var(--color-text-primary)' />,
                activeIcon: <StandaloneBarsRegularIcon iconSize='sm' />,
                label: <Localize i18n_default_text='Menu' />,
                path: routes.menu,
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [active_positions_count, is_logged_in]
    );

    const selectedIndex = React.useMemo(() => {
        if (
            location.pathname === routes.positions ||
            location.pathname === routes.profit ||
            location.pathname === routes.statement
        ) {
            return -1; // No icon highlighted for report sub-routes
        }
        if (matchPath(location.pathname, { path: routes.contract, exact: true })) {
            return -1; // No icon highlighted for contract details page
        }
        const idx = bottomNavItems.findIndex(item => item.path === location.pathname);
        return idx > -1 ? idx : 1; // Default to Trade
    }, [bottomNavItems, location.pathname]);

    const handleSelect = (index: number) => {
        const item = bottomNavItems[index];

        if (item.action === 'home') {
            history.push('/home');
            return;
        }

        if (item.path) {
            history.push(item.path);
        }
    };

    return (
        <Navigation.Bottom
            className={classNames('bottom-nav-container', className)}
            onChange={(_, index) => handleSelect(index)}
        >
            {bottomNavItems.map((item, index) => (
                <Navigation.BottomAction
                    key={index}
                    index={index}
                    activeIcon={<></>}
                    icon={index === selectedIndex ? item.activeIcon : item.icon}
                    label={item.label}
                    selected={index === selectedIndex}
                    showLabel
                    className={classNames(
                        'bottom-nav-item',
                        index === selectedIndex && 'bottom-nav-item--active',
                        item.path === routes.trader_positions && 'bottom-nav-item--positions'
                    )}
                />
            ))}
        </Navigation.Bottom>
    );
});

export default BottomNav;
